/**
 * Sharing notifications from the notification socket (`share.*`, plan P1-D2;
 * `EventDrivenStack/docs/websocket-client-guide.md` §3.2 in omicscloud-cdk). Every one is
 * `{type: 'share.<x>', shareId, …}`.
 *
 * They are reasons to refetch the sharing endpoints, never data to apply, and never a change in the
 * user's own files: nothing here touches a My Files listing or the storage usage.
 */
import type {QueryClient} from '@tanstack/react-query'
import {fileQueryKeys, forgetSharePresignedUrls} from '../widgets/core/storage'
import {sharingQueryKeys} from './hooks'
import {shareManagementKeys} from './managementHooks'

export type ShareEventType =
  | 'share.invited'
  | 'share.joined'
  | 'share.members_changed'
  | 'share.access_changed'
  | 'share.removed'
  | 'share.stopped'
  | 'share.moving'
  | 'share.moved'

const KNOWN: readonly string[] = [
  'share.invited',
  'share.joined',
  'share.members_changed',
  'share.access_changed',
  'share.removed',
  'share.stopped',
  'share.moving',
  'share.moved',
]

export interface ShareEvent {
  type: ShareEventType
  shareId: string
  /** The shared folder's name, when the message carries it. */
  name?: string
  /** `share.removed`: `removed` by the owner, or the user `left`. */
  reason?: string
}

/** True for any `share.*` message, known or not: none of them is about the user's own files. */
export function isShareMessage(message: unknown): boolean {
  const type = (message as {type?: unknown} | null)?.type
  return typeof type === 'string' && type.startsWith('share.')
}

/** A `share.*` message this client acts on, or null (unknown types, no usable `shareId`). */
export function parseShareEvent(message: unknown): ShareEvent | null {
  if (!message || typeof message !== 'object') return null
  const m = message as Record<string, unknown>
  if (typeof m.type !== 'string' || !KNOWN.includes(m.type)) return null
  if (typeof m.shareId !== 'string' || !m.shareId) return null
  return {
    type: m.type as ShareEventType,
    shareId: m.shareId,
    ...(typeof m.name === 'string' && m.name ? {name: m.name} : {}),
    ...(typeof m.reason === 'string' && m.reason ? {reason: m.reason} : {}),
  }
}

/** Sent in the page when a share is gone for this user, so an open Files browser can leave it. */
export const SHARE_GONE_EVENT = 'omics:share-gone'

export interface ShareGoneDetail {
  shareId: string
  type: 'share.removed' | 'share.stopped'
  name?: string
  reason?: string
}

export function subscribeShareGone(cb: (detail: ShareGoneDetail) => void): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<ShareGoneDetail>).detail
    if (detail?.shareId) cb(detail)
  }
  window.addEventListener(SHARE_GONE_EVENT, handler)
  return () => window.removeEventListener(SHARE_GONE_EVENT, handler)
}

/** What a browser leaving a share that went away tells the user. */
export function shareGoneMessage(
  detail: Pick<ShareGoneDetail, 'type' | 'name' | 'reason'>
): string {
  const folder = detail.name ? `“${detail.name}”` : 'That shared folder'
  if (detail.type === 'share.stopped') {
    return `${folder} is no longer shared.`
  }
  if (detail.reason === 'left') {
    return `You left ${detail.name ? folder : 'that shared folder'}.`
  }
  return `You no longer have access to ${detail.name ? folder : 'that shared folder'}.`
}

/** Refetch what a sharing notification says changed (websocket guide §3.2). */
export function applyShareEvent(queryClient: QueryClient, event: ShareEvent): void {
  const {shareId} = event
  switch (event.type) {
    case 'share.invited':
      void queryClient.invalidateQueries(shareManagementKeys.pendingInvitations())
      return
    case 'share.joined':
    case 'share.members_changed':
      void queryClient.invalidateQueries(shareManagementKeys.members(shareId))
      void queryClient.invalidateQueries(shareManagementKeys.invitations(shareId))
      void queryClient.invalidateQueries(sharingQueryKeys.shares())
      return
    case 'share.access_changed':
      // The share list carries the user's access, and with it what the browser offers there.
      void queryClient.invalidateQueries(sharingQueryKeys.shares())
      void queryClient.invalidateQueries(shareManagementKeys.share(shareId))
      void queryClient.invalidateQueries(shareManagementKeys.members(shareId))
      return
    case 'share.moving':
      // Its owner started moving it (P1-A14): the share list now says `moving`, so the folder
      // reads as read-only, with a notice, until `share.moved`.
      void queryClient.invalidateQueries(sharingQueryKeys.shares())
      void queryClient.invalidateQueries(shareManagementKeys.share(shareId))
      return
    case 'share.moved':
      // The move ended: moved (a new path, perhaps a new name) or rolled back. Either way it is no
      // longer moving. Members address files by shareId and a relative path, so the listing stays
      // valid, but it is refetched; presigned URLs issued before the move name the old keys.
      void queryClient.invalidateQueries(sharingQueryKeys.shares())
      void queryClient.invalidateQueries(shareManagementKeys.share(shareId))
      void queryClient.invalidateQueries(fileQueryKeys.share(shareId))
      forgetSharePresignedUrls(shareId)
      return
    case 'share.removed':
    case 'share.stopped': {
      // An open browser leaves the share first, then drops its listing (an observed listing would
      // only be fetched again, and fail). Everything not on screen goes now.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent<ShareGoneDetail>(SHARE_GONE_EVENT, {
            detail: {
              shareId,
              type: event.type,
              ...(event.name ? {name: event.name} : {}),
              ...(event.reason ? {reason: event.reason} : {}),
            },
          })
        )
      }
      queryClient.removeQueries({queryKey: fileQueryKeys.share(shareId), type: 'inactive'})
      queryClient.removeQueries({queryKey: shareManagementKeys.share(shareId), type: 'inactive'})
      forgetSharePresignedUrls(shareId)
      void queryClient.invalidateQueries(sharingQueryKeys.shares())
      // The owner's view of it: who was there, and the pending invitations.
      void queryClient.invalidateQueries(shareManagementKeys.share(shareId))
      return
    }
  }
}
